import {Component, Inject, LOCALE_ID, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {HttpErrorResponse} from '@angular/common/http';
import {ClubOverviewService} from '~features/club-overview/club-overview.service';
import {SidebarService} from '~core/components/sidebar/sidebar.service';
import {NotificationType} from '~core/enum';
import {ClubTranslationsService} from '~shared/translations';
import {AuthenticatedUser, Club, Country, SidebarOptions} from '~core/models';
import {ClubService, CountryService, DefaultNotificationService, NavigationService, StorageService} from '~core/services';
import {ModalService} from '~shared/components/elements/global-modal/modal.service';

/**
 * Handles club-overview updating and attributes assignment.
 */
@UntilDestroy()
@Component({
  selector: 'app-club-overview',
  templateUrl: './club.component.html',
  styleUrls: ['./club.component.scss']
})
export class ClubComponent implements OnInit, OnDestroy {
  public loading: boolean = true;
  public authenticatedUser?: AuthenticatedUser;
  public selectedClub: Club;
  public countries: Array<Country>;
  public color: string;

  public i18nDisableClub: any = $localize`:@@general.tooltip.disableClub:Disable club`;
  public i18nEnableClub: string = $localize`:@@general.tooltip.enableClub:Enable club`;
  public i18nDeleteClub: string = $localize`:@@general.tooltip.deleteClub:Delete club`;

  public i18nDeleteSuccess: string = $localize`:@@general.message.deleteClub:The club has been successfully removed`;
  public i18nDeleteModalTitle: any = $localize`:@@general.title.deleteModal:Delete this club?`;
  public i18nDeleteModalParagraph: any = $localize`:@@general.paragraph.deleteModal:This step will remove the selected promoter and all related data entirely from Tixx-CRM (not from external systems like CleverReach). This cant be reverted! Would you like to proceed?`;

  constructor(
    public clubService: ClubService,
    public countryService: CountryService,
    public sidebarService: SidebarService,
    public route: ActivatedRoute,
    private storage: StorageService,
    public navigationService: NavigationService,
    public clubOverviewService: ClubOverviewService,
    private notificationService: DefaultNotificationService,
    private clubTranslations: ClubTranslationsService,
    private modalService: ModalService,
    private router: Router,
    @Inject(LOCALE_ID) public locale: string
  ) {
    this.sidebarService.setSidebar(new SidebarOptions(false, false));
    this.navigationService.setCurrentPage('clubSettings');
    this.navigationService.setCurrentBreadcrumbs(['crm', 'clubSettings']);
    this.storage.castAuthenticatedUser.pipe(untilDestroyed(this))
      .subscribe((user: AuthenticatedUser) => user !== null ? this.authenticatedUser = user : null);
    this.storage.castSelectedClub.pipe(untilDestroyed(this))
      .subscribe(club => this.selectedClub = club);
    this.storage.castCurrentCountries.pipe(untilDestroyed(this))
      .subscribe(countries => this.countries = countries);
  }

  public ngOnInit(): any {
    this.loading = false;

    this.notificationService.showNotification({
      type: NotificationType.WARNING,
      message: this.clubTranslations.i18nWarningCautiousSettings
    });
  }

  /**
   * TODO: Write decumentation
   */
  public ngOnDestroy(): any {
    this.clubOverviewService.toggleAllModal(false);
  }

  /**
   * Retrieves the country name from country code
   */
  public getCountry(key: any): any {
    return this.countryService.getCountry(key, this.countries);
  }

  /**
   * Executes after logo was uploaded
   */
  public logoUploaded(event: string): void {
    if (event === 'uploaded') {
      this.clubOverviewService.togleEditLogoModal(false);
      this.clubOverviewService.toggleUpdateLoading(false);
      // this.loadData();
    } else {
      this.clubOverviewService.toggleUpdateLoading(false);
    }
  }

  public async showDeleteModal(): Promise<any> {
    this.modalService.setModalOptions(this.i18nDeleteModalTitle, this.i18nDeleteModalParagraph);
    const promise = await new Promise<boolean>((resolve, reject) => this.modalService.openModal(resolve, reject));
    if (promise) {
      this.deleteClub();
    }
  }

  deleteClub() {
    this.clubService.deleteClub(this.selectedClub.id).pipe(untilDestroyed(this))
      .subscribe({
        next: data => {
          this.modalService.setStatus(false);
          this.notificationService.showNotification({
            type: NotificationType.SUCCESS,
            message: `${this.i18nDeleteSuccess}`
          });
          this.router.navigate(['/manage/clubs']);
        },
        error: (error: HttpErrorResponse) => {
          console.log(error);
        }
      });
  }

}
